/**
 * Sistema de Logging Seguro - Magistral 1.0
 * 
 * Objetivo: Proteger dados sensíveis (UUIDs, emails, dados médicos) dos logs em produção
 * 
 * Comportamento:
 * - DEV: logs completos sem restrição
 * - PROD: console limpo (sem logs sensíveis)
 * - PROD + debug flag: logs mascarados (estrutura visível, dados ocultos)
 * 
 * @see Docs/PLANO_SEGURANCA_COMPLETO_2026.md
 */

/**
 * Detecta se está em ambiente de desenvolvimento
 */
const isDev = (): boolean => {
  return import.meta.env.DEV || import.meta.env.MODE === 'development';
};

/**
 * Verifica se debug está ativado em produção
 * Métodos de ativação:
 * 1. URL: ?debug=true
 * 2. localStorage: localStorage.setItem('debug_logs', 'true')
 * 3. Variável de ambiente: VITE_DEBUG_LOGS=true
 */
const isDebugEnabled = (): boolean => {
  if (isDev()) return true;
  
  // URL query param
  const urlParams = new URLSearchParams(window.location.search);
  if (urlParams.get('debug') === 'true') return true;
  
  // localStorage
  if (localStorage.getItem('debug_logs') === 'true') return true;
  
  // Variável de ambiente
  if (import.meta.env.VITE_DEBUG_LOGS === 'true') return true;
  
  return false;
};

/**
 * Mascara dados sensíveis mantendo estrutura visível
 */
const maskSensitiveData = (data: any): any => {
  if (data === null || data === undefined) return data;
  
  // String
  if (typeof data === 'string') {
    // UUID pattern
    if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(data)) {
      return '***UUID***';
    }
    // Email pattern
    if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data)) {
      return '***EMAIL***';
    }
    // CPF pattern (xxx.xxx.xxx-xx)
    if (/^\d{3}\.\d{3}\.\d{3}-\d{2}$/.test(data)) {
      return '***CPF***';
    }
    // Telefone pattern
    if (/^\(\d{2}\)\s?\d{4,5}-?\d{4}$/.test(data)) {
      return '***PHONE***';
    }
    return data;
  }
  
  // Array
  if (Array.isArray(data)) {
    return data.map(item => maskSensitiveData(item));
  }
  
  // Object
  if (typeof data === 'object') {
    const masked: any = {};
    for (const key in data) {
      // Campos sensíveis conhecidos
      const sensitiveFields = [
        'id', 'user_id', 'family_id', 'member_id', 'created_by',
        'email', 'cpf', 'phone', 'telefone',
        'password', 'token', 'access_token', 'refresh_token',
        'private_key', 'secret', 'api_key'
      ];
      
      if (sensitiveFields.includes(key.toLowerCase())) {
        masked[key] = `***${key.toUpperCase()}***`;
      } else {
        masked[key] = maskSensitiveData(data[key]);
      }
    }
    return masked;
  }
  
  return data;
};

/**
 * Formata mensagem de log com prefixo e timestamp
 */
const formatMessage = (prefix: string, ...args: any[]): any[] => {
  const timestamp = new Date().toISOString().split('T')[1].slice(0, 12); // HH:mm:ss.SSS
  return [`[${timestamp}] ${prefix}`, ...args];
};

/**
 * Logger seguro
 */
export const logger = {
  /**
   * Log de debug (sempre visível em DEV, mascarado em PROD+debug)
   */
  debug: (...args: any[]): void => {
    if (!isDebugEnabled()) return;
    
    if (isDev()) {
      console.log(...formatMessage('🔵 [DEBUG]', ...args));
    } else {
      console.log(...formatMessage('🔵 [DEBUG]', ...args.map(maskSensitiveData)));
    }
  },
  
  /**
   * Log de informação (sempre visível em DEV, mascarado em PROD+debug)
   */
  info: (...args: any[]): void => {
    if (!isDebugEnabled()) return;
    
    if (isDev()) {
      console.log(...formatMessage('ℹ️ [INFO]', ...args));
    } else {
      console.log(...formatMessage('ℹ️ [INFO]', ...args.map(maskSensitiveData)));
    }
  },
  
  /**
   * Log de dados sensíveis (sempre mascarado em PROD, completo em DEV)
   * Use para UUIDs, emails, dados de usuários, etc.
   */
  sensitive: (message: string, ...data: any[]): void => {
    if (!isDebugEnabled()) return;
    
    if (isDev()) {
      console.log(...formatMessage(`🔐 [SENSITIVE] ${message}`, ...data));
    } else {
      console.log(...formatMessage(`🔐 [SENSITIVE] ${message}`, ...data.map(maskSensitiveData)));
    }
  },
  
  /**
   * Log de aviso (sempre visível)
   */
  warn: (...args: any[]): void => {
    console.warn(...formatMessage('⚠️ [WARN]', ...args));
  },
  
  /**
   * Log de erro (sempre visível)
   */
  error: (...args: any[]): void => {
    console.error(...formatMessage('❌ [ERROR]', ...args));
  },
  
  /**
   * Log de sucesso (sempre visível em DEV, oculto em PROD)
   */
  success: (...args: any[]): void => {
    if (!isDebugEnabled()) return;
    
    console.log(...formatMessage('✅ [SUCCESS]', ...args));
  },
};

/**
 * Helper para ativar debug em produção
 */
export const enableDebugLogs = (): void => {
  localStorage.setItem('debug_logs', 'true');
  console.log('✅ Debug logs ativados. Recarregue a página.');
};

/**
 * Helper para desativar debug em produção
 */
export const disableDebugLogs = (): void => {
  localStorage.removeItem('debug_logs');
  console.log('❌ Debug logs desativados. Recarregue a página.');
};
